import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { describeError } from '../../errors.js';
import type { AdminHttpClient } from '../../http.js';
import type { SelfSignupFundingWarning, Tenant, ToastMessage } from '../../types.js';
import { toCredits } from '../../utils/format.js';

export function SignupTab({
  http,
  tenant,
  tenants,
  onToast,
  isActive,
}: {
  http: AdminHttpClient;
  tenant?: Tenant;
  tenants: Tenant[];
  onToast: (toast: ToastMessage) => void;
  /** The tab renders inside a `hidden` div and is keyed by tenant, so without
   *  this the platform-global default would be refetched on every tenant
   *  switch and a failure would toast on an unrelated tab. */
  isActive: boolean;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState(tenant);
  const [busy, setBusy] = useState(false);
  const [defaultBusy, setDefaultBusy] = useState(false);
  const [defaultTenantId, setDefaultTenantId] = useState('');
  const [warning, setWarning] = useState<SelfSignupFundingWarning>();

  useEffect(() => {
    setValue(tenant);
    setWarning(undefined);
  }, [tenant]);

  useEffect(() => {
    if (!isActive) return;
    let active = true;
    void http
      .getDefaultSignupTenant()
      .then((setting) => {
        if (active) setDefaultTenantId(setting.tenantId ?? '');
      })
      .catch((error) => {
        if (active)
          onToast({
            tone: 'error',
            message: describeError(error, (key, options) => (options ? t(key, options) : t(key))),
          });
      });
    return () => {
      active = false;
    };
  }, [http, isActive, onToast, t]);

  async function updateDefault(nextTenantId: string) {
    setDefaultBusy(true);
    try {
      const setting = await http.setDefaultSignupTenant(nextTenantId || null);
      setDefaultTenantId(setting.tenantId ?? '');
      onToast({ tone: 'success', message: t('common.changed') });
    } catch (error) {
      onToast({
        tone: 'error',
        message: describeError(error, (key, options) => (options ? t(key, options) : t(key))),
      });
    } finally {
      setDefaultBusy(false);
    }
  }

  async function update(patch: { allowed?: boolean; enabled?: boolean }) {
    if (!value) return;
    setBusy(true);
    try {
      const result = await http.setTenantSelfSignup(value.id, patch);
      setValue(result);
      if (patch.allowed !== undefined) setWarning(patch.allowed ? result.fundedWarning : undefined);
      onToast({ tone: 'success', message: t('common.changed') });
    } catch (error) {
      onToast({
        tone: 'error',
        message: describeError(error, (key, options) => (options ? t(key, options) : t(key))),
      });
    } finally {
      setBusy(false);
    }
  }

  const activeCandidates = tenants.filter(
    (candidate) => candidate.status === 'active' && candidate.id !== '__platform__'
  );

  return (
    <div className="content-stack">
      <section className="panel form-grid">
        <h2>{t('selfSignup.defaultTenantTitle')}</h2>
        <p className="screen-help">{t('selfSignup.defaultTenantHelp')}</p>
        <label>
          <span>{t('selfSignup.defaultTenantLabel')}</span>
          <select
            value={defaultTenantId}
            disabled={defaultBusy}
            onChange={(event) => void updateDefault(event.target.value)}
          >
            <option value="">{t('selfSignup.defaultTenantUnset')}</option>
            {/* A stored default that has since been deactivated (or is absent
                from this list) has no option of its own, and the select would
                then render as "Not configured" while the server still holds
                it. Surface it explicitly instead of misreporting the state. */}
            {defaultTenantId &&
              !activeCandidates.some((candidate) => candidate.id === defaultTenantId) && (
                <option value={defaultTenantId}>
                  {t('selfSignup.defaultTenantUnavailable', { tenantId: defaultTenantId })}
                </option>
              )}
            {activeCandidates.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.name} ({candidate.id})
              </option>
            ))}
          </select>
        </label>
      </section>

      {value ? (
        <section className="panel form-grid">
          <h2>{t('selfSignup.tenantGatesTitle', { name: value.name })}</h2>
          <label className="check-row">
            <input
              type="checkbox"
              checked={value.selfSignupAllowed ?? false}
              disabled={busy}
              onChange={(event) => void update({ allowed: event.target.checked })}
            />
            <span>
              <strong>{t('selfSignup.allowed')}</strong>
              <small>{t('selfSignup.allowedHelp')}</small>
            </span>
          </label>
          <label className="check-row">
            <input
              type="checkbox"
              checked={value.selfSignupEnabled ?? false}
              disabled={busy}
              onChange={(event) => void update({ enabled: event.target.checked })}
            />
            <span>
              <strong>{t('selfSignup.enabled')}</strong>
              <small>
                {value.partnerId
                  ? t('selfSignup.partnerOverride', { partnerId: value.partnerId })
                  : t('selfSignup.enabledHelp')}
              </small>
            </span>
          </label>
          {warning ? (
            <p className="warning" role="alert">
              {t('impactPreview.fundedWarning', {
                exposure: toCredits(warning.theoreticalExposureMillis),
                funded: toCredits(warning.fundedRemainingMillis),
              })}{' '}
              {t('selfSignup.fundedWarningAction')}
            </p>
          ) : null}
        </section>
      ) : (
        <p className="screen-help">{t('platform.selectTenant')}</p>
      )}
    </div>
  );
}
